import React, { useEffect, useRef, useState } from "react"
import { Badge, Button, Card, Col, ProgressBar, Row, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import API from "../../common/API"

type ImportStage = "Queued" | "BulkLoading" | "Completed" | "Failed" | "Cancelled"
type CruzarStage = "Queued" | "BuildingMapping" | "Counting" | "Updating" | "Completed" | "Failed" | "Cancelled"

interface CruzarStatus {
  jobId: string
  stage: CruzarStage
  message?: string
  errorMessage?: string
  rowsUpdated: number
  totalToUpdate: number
  progressPercent: number
  rowsPerSecond: number
  elapsedSeconds: number
  startedAt: string
  finishedAt?: string | null
}

interface CruzarStats {
  total: number
  conCedula: number
  sinCedula: number
  pctConCedula: number
}

interface ImportStatus {
  jobId: string
  fileName: string
  fileSizeMB: number
  stage: ImportStage
  message?: string
  errorMessage?: string
  rowsProcessed: number
  megabytesProcessed: number
  progressPercent: number
  currentLine: number
  rowsPerSecond: number
  megabytesPerSecond: number
  skippedRows: number
  elapsedSeconds: number
  etaSeconds?: number | null
  startedAt: string
  finishedAt?: string | null
}

const formatSeconds = (s?: number | null) => {
  if (s == null || !isFinite(s)) return "—"
  const sec = Math.max(0, Math.round(s))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const r = sec % 60
  if (h > 0) return `${h}h ${m}m ${r}s`
  if (m > 0) return `${m}m ${r}s`
  return `${r}s`
}

const stageVariant = (stage: ImportStage) => {
  switch (stage) {
    case "Completed": return "success"
    case "Failed": return "danger"
    case "Cancelled": return "warning"
    case "BulkLoading": return "info"
    default: return "secondary"
  }
}

const isTerminal = (stage: ImportStage) =>
  stage === "Completed" || stage === "Failed" || stage === "Cancelled"

const isCruzarTerminal = (stage: CruzarStage) =>
  stage === "Completed" || stage === "Failed" || stage === "Cancelled"

const cruzarStageVariant = (stage: CruzarStage) => {
  switch (stage) {
    case "Completed": return "success"
    case "Failed": return "danger"
    case "Cancelled": return "warning"
    case "Updating": return "info"
    case "BuildingMapping": case "Counting": return "primary"
    default: return "secondary"
  }
}

interface Vehiculo {
  idInterno: string
  vin: string
  estado: string
  estadoTransferencia: string
  fechaUltimaActividad: string
  matricula: string
  tipoVehiculo: string
  marca: string
  modelo: string
  anio: number | null
  color: string
  nombrePropietario: string
  direccion: string
  fechaRegistroPlaca: string
  rncImportador: string
  nombreImportador: string
  fechaImportacion: string
  puertoEntrada: string
  cedulaPropietario: string | null
}

const AdminVehiculosComponent: React.FC = () => {
  const { t } = useTranslation("global")
  const [fileName, setFileName] = useState("DatosVehiculos.txt")
  const [batchSize, setBatchSize] = useState(50000)
  const [startFromLine, setStartFromLine] = useState(0)
  const [isStarting, setIsStarting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [isCountingLines, setIsCountingLines] = useState(false)
  const [status, setStatus] = useState<ImportStatus | null>(null)
  const [lineCount, setLineCount] = useState<{ totalLines: number; fileSizeMB: number; elapsedMs: number } | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Cruce state
  const [cruzarStatus, setCruzarStatus] = useState<CruzarStatus | null>(null)
  const [cruzarStats, setCruzarStats] = useState<CruzarStats | null>(null)
  const [isStartingCruce, setIsStartingCruce] = useState(false)
  const [isCancellingCruce, setIsCancellingCruce] = useState(false)
  const cruzarPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Search state
  const [searchName, setSearchName] = useState("")
  const [searchCedula, setSearchCedula] = useState("")
  const [searchVIN, setSearchVIN] = useState("")
  const [searchPage, setSearchPage] = useState(0)
  const [searchResults, setSearchResults] = useState<Vehiculo[]>([])
  const [searchTotal, setSearchTotal] = useState<number | null>(null)
  const [searchPages, setSearchPages] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [activeTab, setActiveTab] = useState<"import" | "records">("records")
  const PAGE_SIZE = 10

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  const pollOnce = async (jobId: string) => {
    try {
      const res = await API.getAction(`Vehiculos/ImportStatus/${jobId}`)
      if (res.data.status === "ok") {
        const s = res.data.response as ImportStatus
        setStatus(s)
        if (isTerminal(s.stage)) {
          stopPolling()
          if (s.stage === "Completed") toast.success(t("Importación completada"))
          else if (s.stage === "Failed") toast.error(t("La importación falló"))
          else if (s.stage === "Cancelled") toast.warning(t("Importación cancelada"))
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  const startPolling = (jobId: string) => {
    stopPolling()
    pollOnce(jobId)
    pollTimerRef.current = setInterval(() => pollOnce(jobId), 3000)
  }

  const stopCruzarPolling = () => {
    if (cruzarPollRef.current) {
      clearInterval(cruzarPollRef.current)
      cruzarPollRef.current = null
    }
  }

  const pollCruzarOnce = async (jobId: string) => {
    try {
      const res = await API.getAction(`Vehiculos/CruzarCedulasStatus/${jobId}`)
      if (res.data.status === "ok") {
        const s = res.data.response as CruzarStatus
        setCruzarStatus(s)
        if (isCruzarTerminal(s.stage)) {
          stopCruzarPolling()
          loadCruzarStats()
          if (s.stage === "Completed") toast.success(t("Cruce de cédulas completado"))
          else if (s.stage === "Failed") toast.error(t("El cruce falló"))
          else if (s.stage === "Cancelled") toast.warning(t("Cruce cancelado"))
        }
      }
    } catch (e) { console.error(e) }
  }

  const loadCruzarStats = async () => {
    try {
      const res = await API.getAction("Vehiculos/CruzarCedulasStats")
      if (res.data.status === "ok") setCruzarStats(res.data.response)
    } catch (e) { console.error(e) }
  }

  const handleStartCruce = async (force = false) => {
    if (!window.confirm(String(t("Se cruzarán los nombres de propietario contra el padrón de Cedulados para derivar cédulas. Este proceso puede tomar 10-30 minutos. ¿Continuar?")))) return
    try {
      setIsStartingCruce(true)
      const res = await API.postAction(`Vehiculos/CruzarCedulasStart?force=${force}`)
      if (res.data.status === "ok") {
        const jobId = res.data.response.jobId as string
        toast.success(t("Cruce iniciado"))
        const init: CruzarStatus = {
          jobId, stage: "BuildingMapping",
          rowsUpdated: 0, totalToUpdate: 0, progressPercent: 0,
          rowsPerSecond: 0, elapsedSeconds: 0, startedAt: res.data.response.startedAt
        }
        setCruzarStatus(init)
        stopCruzarPolling()
        pollCruzarOnce(jobId)
        cruzarPollRef.current = setInterval(() => pollCruzarOnce(jobId), 4000)
      } else {
        toast.error(res.data.response ?? t("Error al iniciar cruce"))
      }
    } catch (e: any) {
      if (e?.response?.status === 409) {
        if (window.confirm(String(t("Ya hay un cruce en curso. ¿Forzar?")))) { handleStartCruce(true); return }
      }
      toast.error(e?.response?.data?.response ?? e.message ?? t("Error al iniciar cruce"))
    } finally {
      setIsStartingCruce(false)
    }
  }

  const handleCancelCruce = async () => {
    if (!cruzarStatus?.jobId) return
    if (!window.confirm(String(t("¿Cancelar el cruce en curso?")))) return
    try {
      setIsCancellingCruce(true)
      await API.postAction(`Vehiculos/CruzarCedulasCancel/${cruzarStatus.jobId}`)
      toast.info(t("Cancelación solicitada"))
    } catch (e: any) {
      toast.error(e?.message ?? t("Error al cancelar"))
    } finally {
      setIsCancellingCruce(false)
    }
  }

  useEffect(() => () => { stopPolling(); stopCruzarPolling() }, []) // eslint-disable-line

  useEffect(() => {
    if (activeTab === "records" && searchTotal === null) handleSearch(0)
    if (activeTab === "import" && cruzarStats === null) loadCruzarStats()
  }, [activeTab]) // eslint-disable-line

  const handleCountLines = async () => {
    try {
      setIsCountingLines(true)
      setLineCount(null)
      const res = await API.getAction(`Vehiculos/CountLines?fileName=${encodeURIComponent(fileName)}`)
      if (res.data.status === "ok") {
        setLineCount(res.data.response)
        toast.success(t("Conteo completado"))
      } else {
        toast.error(res.data.response ?? t("Error al contar líneas"))
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.response ?? e.message ?? t("Error al contar líneas"))
    } finally {
      setIsCountingLines(false)
    }
  }

  const handleStart = async (force = false) => {
    if (
      !window.confirm(
        String(t("Se importará DatosVehiculos.txt a la tabla Vehiculos. La tabla debe existir (ejecuta el DDL de database.sql primero). ¿Continuar?"))
      )
    ) return

    try {
      setIsStarting(true)
      const params =
        `fileName=${encodeURIComponent(fileName)}` +
        `&batchSize=${batchSize}` +
        `&startFromLine=${startFromLine}` +
        `&force=${force}`
      const res = await API.postAction(`Vehiculos/ImportVehiculosFast?${params}`)
      if (res.data.status === "ok") {
        const jobId = res.data.response.jobId as string
        toast.success(t("Importación iniciada"))
        const initialStatus: ImportStatus = {
          jobId,
          fileName,
          fileSizeMB: res.data.response.fileSizeMB,
          stage: "BulkLoading",
          rowsProcessed: 0,
          megabytesProcessed: 0,
          progressPercent: 0,
          currentLine: startFromLine,
          rowsPerSecond: 0,
          megabytesPerSecond: 0,
          elapsedSeconds: 0,
          startedAt: res.data.response.startedAt,
          skippedRows: 0,
        }
        setStatus(initialStatus)
        startPolling(jobId)
      } else {
        toast.error(res.data.response ?? t("Error al iniciar"))
      }
    } catch (e: any) {
      if (e?.response?.status === 409) {
        if (window.confirm(String(t("Ya hay un job corriendo (o zombi). ¿Forzar?")))) {
          handleStart(true)
          return
        }
      }
      toast.error(e?.response?.data?.response ?? e.message ?? t("Error al iniciar"))
    } finally {
      setIsStarting(false)
    }
  }

  const handleClearJobs = async () => {
    try {
      setIsClearing(true)
      const res = await API.postAction("Vehiculos/ImportClearJobs")
      if (res.data.status === "ok") {
        toast.info(`${t("Jobs limpiados")}: ${res.data.response.removed ?? 0}`)
      }
    } catch (e: any) {
      toast.error(e?.message ?? t("Error al limpiar jobs"))
    } finally {
      setIsClearing(false)
    }
  }

  const handleCancel = async () => {
    if (!status?.jobId) return
    if (!window.confirm(String(t("¿Cancelar la importación en curso?")))) return
    try {
      setIsCancelling(true)
      const res = await API.postAction(`Vehiculos/ImportCancel/${status.jobId}`)
      if (res.data.status === "ok") toast.info(t("Cancelación solicitada"))
    } catch (e: any) {
      toast.error(e?.message ?? t("Error al cancelar"))
    } finally {
      setIsCancelling(false)
    }
  }

  const handleSearch = async (page = 0) => {
    try {
      setIsSearching(true)
      setSearchPage(page)
      const res = await API.postAction("Vehiculos/List", {
        search: searchName || null,
        cedula: searchCedula || null,
        vin: searchVIN || null,
        page,
        quantity: PAGE_SIZE,
      })
      if (res.data.status === "ok") {
        setSearchResults(res.data.response)
        setSearchTotal(res.data.quantityOfRecords)
        setSearchPages(res.data.quantityOfPages)
      } else {
        toast.error(res.data.response ?? t("Error en búsqueda"))
      }
    } catch (e: any) {
      toast.error(e?.message ?? t("Error en búsqueda"))
    } finally {
      setIsSearching(false)
    }
  }

  const isRunning = status != null && !isTerminal(status.stage)

  return (
    <div className="my-4">
      <h4 className="mb-3">
        <i className="fas fa-car me-2" />
        {t("Vehículos")}
      </h4>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "records" ? "active" : ""}`}
            onClick={() => setActiveTab("records")}
          >
            <i className="fas fa-search me-2" />{t("Registros")}
            {searchTotal !== null && (
              <span className="badge bg-secondary ms-2">{searchTotal.toLocaleString()}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "import" ? "active" : ""}`}
            onClick={() => setActiveTab("import")}
          >
            <i className="fas fa-upload me-2" />{t("Importación")}
            {isRunning && <span className="badge bg-info ms-2">{t("En curso")}</span>}
          </button>
        </li>
      </ul>

      {activeTab === "records" && (
        <Card>
          <Card.Body>
            <Row className="g-2 mb-3">
              <Col md={4}>
                <input
                  type="text"
                  className="form-control"
                  placeholder={String(t("Nombre del propietario"))}
                  value={searchName}
                  onChange={e => setSearchName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch(0)}
                />
              </Col>
              <Col md={3}>
                <input
                  type="text"
                  className="form-control"
                  placeholder={String(t("Cédula"))}
                  value={searchCedula}
                  onChange={e => setSearchCedula(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch(0)}
                />
              </Col>
              <Col md={3}>
                <input
                  type="text"
                  className="form-control"
                  placeholder={String(t("VIN / ID Interno"))}
                  value={searchVIN}
                  onChange={e => setSearchVIN(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch(0)}
                />
              </Col>
              <Col md={2}>
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => handleSearch(0)}
                  disabled={isSearching}
                >
                  {isSearching
                    ? <span className="spinner-border spinner-border-sm" />
                    : <><i className="fas fa-search me-1" />{t("Buscar")}</>
                  }
                </Button>
              </Col>
            </Row>

            {searchTotal !== null && (
              <div className="text-muted small mb-2">
                {searchTotal.toLocaleString()} {t("registros encontrados")}
                {searchPages > 1 && ` — ${t("página")} ${searchPage + 1} / ${searchPages}`}
              </div>
            )}

            {searchResults.length > 0 && (
              <>
                <div style={{ overflowX: "auto" }}>
                  <Table size="sm" bordered striped hover className="small mb-2">
                    <thead className="table-dark">
                      <tr>
                        <th>{t("Propietario")}</th>
                        <th>{t("Cédula")}</th>
                        <th>{t("Marca / Modelo")}</th>
                        <th>{t("Año")}</th>
                        <th>{t("Color")}</th>
                        <th>{t("VIN")}</th>
                        <th>{t("Matrícula")}</th>
                        <th>{t("Estado")}</th>
                        <th>{t("Puerto")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((v, i) => (
                        <tr key={i}>
                          <td>
                            <div className="fw-semibold">{v.nombrePropietario}</div>
                            <small className="text-muted">{v.direccion}</small>
                          </td>
                          <td>
                            {v.cedulaPropietario
                              ? (
                                <Badge
                                  bg="success"
                                  style={{ cursor: "pointer" }}
                                  title={t("Ver reporte") as string}
                                  onClick={() => window.open(`/record/${v.cedulaPropietario}`, "_blank")}
                                >
                                  {v.cedulaPropietario}
                                </Badge>
                              )
                              : <span className="text-muted">—</span>}
                          </td>
                          <td>{v.marca} {v.modelo}</td>
                          <td>{v.anio ?? "—"}</td>
                          <td>{v.color}</td>
                          <td><small>{v.vin}</small></td>
                          <td>{v.matricula}</td>
                          <td>
                            <Badge bg={v.estado === "ACTIVO" ? "success" : "warning"} className="me-1">
                              {v.estado}
                            </Badge>
                            <br />
                            <small className="text-muted">{v.estadoTransferencia}</small>
                          </td>
                          <td><small>{v.puertoEntrada}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {searchPages > 1 && (
                  <div className="d-flex gap-2 justify-content-center mt-2">
                    <Button variant="outline-secondary" size="sm"
                      disabled={searchPage === 0 || isSearching}
                      onClick={() => handleSearch(searchPage - 1)}>
                      ‹ {t("Anterior")}
                    </Button>
                    <span className="align-self-center small">{searchPage + 1} / {searchPages}</span>
                    <Button variant="outline-secondary" size="sm"
                      disabled={searchPage >= searchPages - 1 || isSearching}
                      onClick={() => handleSearch(searchPage + 1)}>
                      {t("Siguiente")} ›
                    </Button>
                  </div>
                )}
              </>
            )}

            {searchTotal === 0 && (
              <div className="text-muted text-center py-3">
                <i className="fas fa-inbox me-2" />{t("Sin resultados")}
              </div>
            )}

            {searchTotal === null && (
              <div className="text-muted text-center py-5">
                <i className="fas fa-car fa-2x mb-2 d-block" />
                {t("Busca por nombre, cédula o VIN para ver los registros importados")}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {activeTab === "import" && (
      <div>
      <div className="alert alert-info">
        <i className="fas fa-info-circle me-2" />
        {t(
          "Archivo fixed-width (999 chars/línea, Latin-1). Cada lote se commitea independiente — puedes reanudar desde la línea indicada si se interrumpe. CedulaPropietario se deriva automáticamente cuando el propietario importó su propio vehículo."
        )}
      </div>

      <div className="alert alert-warning">
        <i className="fas fa-database me-2" />
        <strong>{t("Prerequisito")}: </strong>
        {t("Ejecutar primero el DDL de Vehiculos en database.sql (DROP + CREATE TABLE + índices).")}
      </div>

      <Card className="mb-3">
        <Card.Header className="bg-light">
          <strong>{t("1. Configuración")}</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={5}>
              <label className="form-label">{t("Nombre del archivo")}</label>
              <input
                type="text"
                className="form-control"
                value={fileName}
                onChange={e => setFileName(e.target.value)}
                disabled={isRunning}
              />
              <small className="text-muted">{t("Debe estar en wwwroot/supportDocuments del API")}</small>
            </Col>
            <Col md={3}>
              <label className="form-label">{t("Tamaño de batch")}</label>
              <input
                type="number"
                className="form-control"
                value={batchSize}
                onChange={e => setBatchSize(Number(e.target.value) || 50000)}
                min={1000}
                max={500000}
                step={10000}
                disabled={isRunning}
              />
              <small className="text-muted">{t("Recomendado: 50000 (archivo es ~6 GB)")}</small>
            </Col>
            <Col md={4}>
              <label className="form-label">&nbsp;</label>
              <Button
                variant="outline-secondary"
                className="d-block w-100"
                onClick={handleCountLines}
                disabled={isCountingLines || isRunning}
              >
                {isCountingLines ? (
                  <><span className="spinner-border spinner-border-sm me-2" />{t("Contando...")}</>
                ) : (
                  <><i className="fas fa-hashtag me-2" />{t("Contar líneas")}</>
                )}
              </Button>
            </Col>
          </Row>
          <Row className="g-3 mt-1">
            <Col md={4}>
              <label className="form-label">{t("Reanudar desde línea")}</label>
              <input
                type="number"
                className="form-control"
                value={startFromLine}
                onChange={e => setStartFromLine(Math.max(0, Number(e.target.value) || 0))}
                min={0}
                step={100000}
                disabled={isRunning}
              />
              <small className="text-muted">{t("0 = inicio del archivo")}</small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {lineCount && (
        <div className="alert alert-secondary d-flex flex-wrap gap-4 align-items-center mb-3">
          <span>
            <i className="fas fa-hashtag me-2 text-primary" />
            <strong>{t("Total líneas")}:</strong>{" "}
            <span className="fs-5 fw-bold">{lineCount.totalLines.toLocaleString()}</span>
          </span>
          <span>
            <strong>{t("Tamaño")}:</strong> {lineCount.fileSizeMB.toLocaleString()} MB
          </span>
          <span className="text-muted">
            <i className="fas fa-clock me-1" />{lineCount.elapsedMs.toLocaleString()} ms
          </span>
        </div>
      )}

      <Card className="mb-3">
        <Card.Header className="bg-light">
          <strong>{t("2. Importación")}</strong>
        </Card.Header>
        <Card.Body>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="primary"
              size="lg"
              onClick={() => handleStart(false)}
              disabled={isStarting || isRunning}
            >
              {isStarting ? (
                <><span className="spinner-border spinner-border-sm me-2" />{t("Iniciando...")}</>
              ) : (
                <><i className="fas fa-play me-2" />{t("Iniciar importación")}</>
              )}
            </Button>

            {isRunning && (
              <Button variant="outline-danger" size="lg" onClick={handleCancel} disabled={isCancelling}>
                <i className="fas fa-stop me-2" />
                {isCancelling ? t("Cancelando...") : t("Cancelar")}
              </Button>
            )}

            <Button
              variant="outline-secondary"
              size="lg"
              onClick={handleClearJobs}
              disabled={isClearing || isRunning}
              title={String(t("Limpia jobs terminados y zombis (>15 min sin avance)"))}
            >
              <i className="fas fa-broom me-2" />
              {isClearing ? t("Limpiando...") : t("Limpiar jobs")}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {status && (
        <Card>
          <Card.Header className="bg-light d-flex justify-content-between align-items-center">
            <strong>{t("3. Progreso")}</strong>
            <span className={`badge bg-${stageVariant(status.stage)}`}>{status.stage}</span>
          </Card.Header>
          <Card.Body>
            <ProgressBar
              now={status.progressPercent}
              label={`${status.progressPercent.toFixed(1)}%`}
              variant={stageVariant(status.stage)}
              animated={isRunning}
              striped={isRunning}
              style={{ height: 28 }}
              className="mb-3"
            />

            <Row className="g-3">
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("Filas importadas")}</small>
                <strong>{status.rowsProcessed.toLocaleString()}</strong>
              </Col>
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("MB procesados")}</small>
                <strong>{status.megabytesProcessed.toLocaleString()} / {status.fileSizeMB.toLocaleString()}</strong>
              </Col>
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("Velocidad")}</small>
                <strong>{status.rowsPerSecond.toLocaleString()} {t("filas/s")}</strong>
                <br />
                <small>{status.megabytesPerSecond.toLocaleString()} MB/s</small>
              </Col>
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("Tiempo")}</small>
                <strong>{formatSeconds(status.elapsedSeconds)}</strong>
                <br />
                <small>ETA: {isRunning ? formatSeconds(status.etaSeconds) : "—"}</small>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={6}>
                <small className="text-muted d-block">{t("Última línea procesada")}</small>
                <strong>{status.currentLine.toLocaleString()}</strong>
                <br />
                <small className="text-muted">{t("Úsala en 'Reanudar desde línea' si necesitas continuar")}</small>
              </Col>
              {(status.skippedRows ?? 0) > 0 && (
                <Col md={6}>
                  <small className="text-muted d-block">{t("Filas omitidas (longitud incorrecta)")}</small>
                  <strong className="text-warning">{status.skippedRows.toLocaleString()}</strong>
                </Col>
              )}
            </Row>

            {status.errorMessage && (
              <div className="alert alert-danger mt-3 mb-0">
                <strong>{t("Error")}:</strong> {status.errorMessage}
              </div>
            )}
            {status.message && status.stage === "Completed" && (
              <div className="alert alert-success mt-3 mb-0">
                <i className="fas fa-check-circle me-2" />
                {status.message}
              </div>
            )}
          </Card.Body>
        </Card>
      )}
      {/* ── Cruce de Cédulas ─────────────────────────────────────────── */}
      <hr className="my-4" />
      <h5 className="mb-3">
        <i className="fas fa-link me-2 text-success" />
        {t("Cruce de Cédulas por Nombre")}
      </h5>

      {cruzarStats && (
        <div className="alert alert-secondary mb-3">
          <Row className="g-3 align-items-center">
            <Col md={3} sm={6}>
              <small className="text-muted d-block">{t("Total vehículos")}</small>
              <strong>{cruzarStats.total.toLocaleString()}</strong>
            </Col>
            <Col md={3} sm={6}>
              <small className="text-muted d-block">{t("Con cédula vinculada")}</small>
              <strong className="text-success">{cruzarStats.conCedula.toLocaleString()}</strong>
            </Col>
            <Col md={3} sm={6}>
              <small className="text-muted d-block">{t("Sin cédula")}</small>
              <strong className="text-warning">{cruzarStats.sinCedula.toLocaleString()}</strong>
            </Col>
            <Col md={3} sm={6}>
              <small className="text-muted d-block">{t("Cobertura")}</small>
              <strong>{cruzarStats.pctConCedula.toFixed(1)}%</strong>
              <ProgressBar now={cruzarStats.pctConCedula} variant="success" style={{ height: 6 }} className="mt-1" />
            </Col>
          </Row>
        </div>
      )}

      <Card className="mb-3">
        <Card.Header className="bg-light">
          <strong>{t("Derivar cédulas desde el padrón")}</strong>
        </Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            {t("Cruza NombrePropietario contra Cedulados para llenar CedulaPropietario donde haya exactamente 1 cédula con ese nombre. Solo actualiza registros sin cédula. El proceso dura 10-30 minutos.")}
          </p>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="success"
              onClick={() => handleStartCruce(false)}
              disabled={isStartingCruce || (cruzarStatus != null && !isCruzarTerminal(cruzarStatus.stage))}
            >
              {isStartingCruce ? (
                <><span className="spinner-border spinner-border-sm me-2" />{t("Iniciando...")}</>
              ) : (
                <><i className="fas fa-link me-2" />{t("Iniciar cruce")}</>
              )}
            </Button>
            {cruzarStatus != null && !isCruzarTerminal(cruzarStatus.stage) && (
              <Button variant="outline-danger" onClick={handleCancelCruce} disabled={isCancellingCruce}>
                <i className="fas fa-stop me-2" />
                {isCancellingCruce ? t("Cancelando...") : t("Cancelar")}
              </Button>
            )}
          </div>

          {cruzarStatus && (
            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">{cruzarStatus.message}</small>
                <span className={`badge bg-${cruzarStageVariant(cruzarStatus.stage)}`}>{cruzarStatus.stage}</span>
              </div>
              <ProgressBar
                now={cruzarStatus.progressPercent}
                label={cruzarStatus.totalToUpdate > 0 ? `${cruzarStatus.progressPercent.toFixed(1)}%` : undefined}
                variant={cruzarStageVariant(cruzarStatus.stage)}
                animated={!isCruzarTerminal(cruzarStatus.stage)}
                striped={!isCruzarTerminal(cruzarStatus.stage)}
                style={{ height: 24 }}
                className="mb-2"
              />
              <Row className="g-2">
                <Col sm={4}>
                  <small className="text-muted d-block">{t("Actualizados")}</small>
                  <strong>{cruzarStatus.rowsUpdated.toLocaleString()}</strong>
                  {cruzarStatus.totalToUpdate > 0 && (
                    <small className="text-muted"> / {cruzarStatus.totalToUpdate.toLocaleString()}</small>
                  )}
                </Col>
                <Col sm={4}>
                  <small className="text-muted d-block">{t("Velocidad")}</small>
                  <strong>{cruzarStatus.rowsPerSecond.toLocaleString()} {t("filas/s")}</strong>
                </Col>
                <Col sm={4}>
                  <small className="text-muted d-block">{t("Tiempo")}</small>
                  <strong>{formatSeconds(cruzarStatus.elapsedSeconds)}</strong>
                </Col>
              </Row>
              {cruzarStatus.errorMessage && (
                <div className="alert alert-danger mt-2 mb-0 py-2">
                  <small><strong>{t("Error")}:</strong> {cruzarStatus.errorMessage}</small>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
      </div>
      )}
    </div>
  )
}

export default AdminVehiculosComponent
